import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Chat as ChatIcon } from "../../icons";
import { EventAvailable as EventAvailableIcon } from "../../icons";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, functions } from "../../firebase";
import { T } from "../Translate";
import PageHeader from "../PageHeader";
import PaypalButton from "../../components/PaypalButton";
import { notifyUser } from "../../utils/notifications";
import { pageContentSx } from "../../pageLayout";

// Parte del total que se paga al reservar. Mismo valor que
// STAY_DEPOSIT_RATE en functions/payments.js.
export const STAY_DEPOSIT_RATE = 0.3;
const MAX_STAY_NIGHTS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const addDays = (day, n) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
const nightsBetween = (from, to) =>
  from && to ? Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS) : 0;
const formatDay = (day) => new Date(`${day}T12:00:00`).toLocaleDateString();

// Reserva de un alojamiento (places con category "alojamiento" y
// paypalEmail): el huésped elige fechas, paga la seña directo al PayPal del
// anfitrión y recordPayPalPayment (kind stayReservation) verifica el pago,
// guarda la reserva en /trips y bloquea las fechas en place.bookedRanges.
// El resto se arregla con el anfitrión por chat.
const StayCheckout = () => {
  const { placeId } = useParams();
  const navigate = useNavigate();
  const [place, setPlace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState(1);
  const [result, setResult] = useState(null); // "success" | "unsaved"

  useEffect(() => {
    let active = true;
    getDoc(doc(db, "places", placeId))
      .then((snap) => active && setPlace(snap.exists() ? { id: snap.id, ...snap.data() } : null))
      .catch((e) => console.error("Error al obtener el alojamiento:", e))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [placeId]);

  const today = todayStr();
  const nights = nightsBetween(checkIn, checkOut);
  const nightlyPrice = Number(place?.price) || 0;
  const total = Number((nightlyPrice * Math.max(nights, 0)).toFixed(2));
  const deposit = Number((total * STAY_DEPOSIT_RATE).toFixed(2));
  const bookedRanges = useMemo(
    () => [...(place?.bookedRanges || [])].filter((r) => r.checkOut > today).sort((a, b) => a.checkIn.localeCompare(b.checkIn)),
    [place, today]
  );
  const overlaps = !!checkIn && !!checkOut && bookedRanges.some((r) => r.checkIn < checkOut && checkIn < r.checkOut);

  let dateError = null;
  if (checkIn && checkIn < today) dateError = <T>La fecha de llegada ya pasó.</T>;
  else if (checkIn && checkOut && nights < 1) dateError = <T>La salida tiene que ser después de la llegada.</T>;
  else if (nights > MAX_STAY_NIGHTS) dateError = <T>La estadía no puede superar los 90 días.</T>;
  else if (overlaps) dateError = <T>Esas fechas ya están reservadas.</T>;
  const canPay = nights >= 1 && !dateError && deposit > 0;

  const isOwn = place?.uid === auth.currentUser?.uid;
  const acceptsReservations = place?.category === "alojamiento" && !!place?.paypalEmail && !place?.sourceUrl;

  const handleApprove = async (details) => {
    try {
      await functions.httpsCallable("recordPayPalPayment")({
        kind: "stayReservation",
        targetId: placeId,
        orderId: details.id,
        extra: { checkIn, checkOut, guests },
      });
      notifyUser({
        userId: place.uid,
        companyId: auth.currentUser?.uid,
        category: "messages",
        type: "message",
        message: `Nueva reserva en "${place.title}" del ${formatDay(checkIn)} al ${formatDay(checkOut)}.`,
        postId: placeId,
      });
      setResult("success");
    } catch (error) {
      // La seña ya se cobró en PayPal: aunque no se haya guardado, el
      // huésped tiene que poder hablar con el anfitrión.
      console.error("Error al registrar la reserva:", error);
      setResult("unsaved");
    }
  };

  const goToChat = () => navigate(`/chatbox/viaje/${placeId}/${place.uid}`);

  let content;
  if (loading) {
    content = (
      <Stack alignItems="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  } else if (!place || !acceptsReservations) {
    content = <Alert severity="error"><T>Este alojamiento no se puede reservar online. Consultale al anfitrión por chat.</T></Alert>;
  } else if (isOwn) {
    content = <Alert severity="info"><T>Este es tu alojamiento: no podés reservarlo.</T></Alert>;
  } else {
    content = (
      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            {place.images?.[0] && (
              <Box
                component="img"
                src={place.images[0]}
                alt={place.title}
                sx={{ width: 72, height: 72, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{place.title}</Typography>
              <Typography variant="body2" color="text.secondary">
                USD {nightlyPrice.toLocaleString()} <T>por noche</T>
              </Typography>
            </Box>
          </Stack>

          {result ? (
            <>
              <Alert severity={result === "success" ? "success" : "warning"}>
                {result === "success" ? (
                  <T>¡Reserva confirmada! Ya le avisamos al anfitrión.</T>
                ) : (
                  <T>La seña se pagó, pero no se pudo guardar la reserva. Escribile al anfitrión con el comprobante de PayPal.</T>
                )}
              </Alert>
              <Button fullWidth variant="contained" startIcon={<ChatIcon />} onClick={goToChat} sx={{ textTransform: "none" }}>
                <T>Hablar con el anfitrión</T>
              </Button>
              <Button onClick={() => navigate("/my-purchases")} sx={{ textTransform: "none" }}>
                <T>Ver mis reservas</T>
              </Button>
            </>
          ) : (
            <>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  label={<T>Llegada</T>}
                  type="date"
                  value={checkIn}
                  onChange={(e) => {
                    setCheckIn(e.target.value);
                    if (e.target.value && (!checkOut || checkOut <= e.target.value)) setCheckOut(addDays(e.target.value, 1));
                  }}
                  inputProps={{ min: today }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label={<T>Salida</T>}
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  inputProps={{ min: checkIn ? addDays(checkIn, 1) : today }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label={<T>Huéspedes</T>}
                  type="number"
                  value={guests}
                  onChange={(e) => setGuests(Math.min(Math.max(parseInt(e.target.value, 10) || 1, 1), 50))}
                  inputProps={{ min: 1, max: 50 }}
                  sx={{ minWidth: { sm: 110 } }}
                />
              </Stack>

              {bookedRanges.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                    <EventAvailableIcon sx={{ fontSize: 16 }} /> <T>Fechas ya reservadas:</T>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {bookedRanges.map((r) => `${formatDay(r.checkIn)} – ${formatDay(r.checkOut)}`).join(" · ")}
                  </Typography>
                </Box>
              )}

              {dateError && <Alert severity="error">{dateError}</Alert>}

              {canPay && (
                <Box sx={{ p: 2, borderRadius: 3, bgcolor: "action.hover" }}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">
                      USD {nightlyPrice.toLocaleString()} × {nights} {nights === 1 ? <T>noche</T> : <T>noches</T>}
                    </Typography>
                    <Typography variant="body2">USD {total.toFixed(2)}</Typography>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Stack direction="row" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 700 }}><T>Seña a pagar ahora (30%)</T></Typography>
                    <Typography sx={{ fontWeight: 700 }}>USD {deposit.toFixed(2)}</Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    <T>El resto</T> (USD {(total - deposit).toFixed(2)}) <T>se lo pagás al anfitrión como acuerden.</T>
                  </Typography>
                </Box>
              )}

              <Alert severity="info">
                <T>La seña va directo al PayPal del anfitrión. Después de reservar, coordiná la llegada con él por chat.</T>
              </Alert>

              {canPay && (
                <PaypalButton
                  amount={deposit}
                  currency="USD"
                  description={`Seña reserva: ${place.title} (${checkIn} a ${checkOut})`}
                  clientId={process.env.REACT_APP_PAYPAL_CLIENT_ID}
                  payeeEmail={place.paypalEmail}
                  metadata={{ customId: placeId }}
                  onApprove={handleApprove}
                />
              )}
            </>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <PageHeader title={<T>Reservar</T>} subtitle={place?.title} />
      <Container maxWidth="sm" sx={{ mt: 4, mb: 12 }}>
        {content}
      </Container>
    </Container>
  );
};

export default StayCheckout;
